import { useState, useCallback, useEffect, useRef, useMemo, type RefObject } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { CheckSquare, ChevronDown, ImageOff, Loader2, SlidersHorizontal, X } from 'lucide-react'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { useStatusPoll } from '@/shared/hooks/useStatusPoll'
import { STATUS_POLL_INTERVAL_MS } from '@/shared/constants/statusPolling'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Input } from '@/shared/ui/input'
import { Sheet, SheetContent, SheetTrigger } from '@/shared/ui/sheet'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { MultiSelectCombobox, type MultiSelectOption } from '@/shared/ui/multi-select-combobox'
import DataPagination from '@/shared/ui/DataPagination'
import { t } from '@/shared/lib/i18n'
import { appendArrayOrgFilterParams, clearOrgFiltersParams } from '@/shared/lib/persistedOrgFilters'
import {
  buildOrgCascadeChangeHandlers,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
} from '@/shared/lib/orgMultiSelectCascade'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { cn } from '@/shared/lib/utils'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import { HideNonComplianceToggle } from '@/pages/Dashboard/Tasks/HideNonComplianceToggle'
import {
  readHideNonComplianceAll,
  writeHideNonComplianceAll,
} from '@/pages/Dashboard/Tasks/hideNonComplianceAllSession'
import {
  TASKS_DASHBOARD_STATUS_POLL_OPTIONS,
  reloadTasksDashboardProps,
} from '@/shared/lib/reloadTasksDashboardProps'
import {
  tasksDashboardPath,
  tasksDashboardCalendarPath,
  tasksDashboardOverflowPath,
  tasksDashboardNcOverflowPath,
} from '@/shared/lib/routes'
import apiClient from '@/shared/lib/apiClient'
import { useInstrumentTail, useNearViewport } from '@/pages/Dashboard/Tasks/useInstrumentTail'
import DashboardTabs from '@/pages/Compliance/DashboardTabs'
import { TaskQueueCard } from '@/features/task-queue-card'
import { NonComplianceCard } from '@/features/non-compliance-actions'
import InstrumentCalendarViewPanel from '@/pages/Instruments/components/ReportsTab/InstrumentCalendarViewPanel'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebDashboardTaskQueueCard from '@/shared/types/web/WebDashboardTaskQueueCard'
import type WebNonComplianceCard from '@/shared/types/web/WebNonComplianceCard'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import type { LoggerOption } from '@/features/task-logging/TaskLogForm'

interface SelectOption {
  value: number
  label: string
  siteId?: number | null
  departmentId?: number | null
}

interface TagOption {
  value: number
  label: string
}

interface Filters {
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  search?: string
  tagIds?: number[]
  instrumentIds?: number[]
}

interface Props {
  taskRecords: WebDashboardTaskQueueCard[]
  ncRecords: WebNonComplianceCard[]
  taskCountsByInstrument: Record<number, number>
  ncCountsByInstrument: Record<number, number>
  dashboardFingerprint: string
  meta: PaginationMeta
  filters: Filters
  siteOptions: SelectOption[]
  departmentOptions: SelectOption[]
  unitOptions: SelectOption[]
  tagOptions: TagOption[]
  instrumentOptions: MultiSelectOption[]
  hasPendingTasks: boolean
  loggerOptions: LoggerOption[]
  logAuthenticationCode: string | null
  instrumentTagsMap: Record<number, Array<{ id: number; name: string }>>
  [key: string]: unknown
}

const STATUS_SECTIONS = [
  { code: 'ODE', label: () => t('taskQueue.sections.overdue') },
  { code: 'DNW', label: () => t('taskQueue.sections.dueNow') },
  { code: 'RDY', label: () => t('taskQueue.sections.ready') },
] as const

type TaskQueueSectionCode = (typeof STATUS_SECTIONS)[number]['code']

// Queue UTD rows are only ever failed-QC tasks (REBUILD-206): they are actionable but have no
// dedicated section, so they fold into Overdue to keep Log Task (re-run) available. An open NC
// never produces a UTD row — the NC card stands alone and the log row waits for the next
// occurrence's own window (REBUILD-547).
const tasksForSection = (
  sectionCode: TaskQueueSectionCode,
  tasks: WebDashboardTaskQueueCard[],
): WebDashboardTaskQueueCard[] =>
  tasks.filter((record) =>
    sectionCode === 'ODE'
      ? record.statusCode === 'ODE' || record.statusCode === 'UTD'
      : record.statusCode === sectionCode,
  )

// Builds the calendar fetch callback for the dashboard's InstrumentCalendarViewPanel.
// Hits the dashboard calendar endpoint (same service as the Reports tab) so the dashboard
// calendar gains unbounded month navigation + day-click, matching legacy DASHBOARD_INSTRUMENTS.
const buildDashboardCalendarFetchFn =
  (instrumentId: number) =>
  (from: string, to: string, taskIds: number[]): Promise<WebInstrumentCalendarView | null> =>
    apiClient
      .get<WebInstrumentCalendarView>(tasksDashboardCalendarPath(), {
        params: {
          instrument_id: instrumentId,
          from,
          to,
          ...(taskIds.length > 0 ? { task_ids: taskIds } : {}),
        },
      })
      .then((response) => response.data)
      .catch((error: unknown) => {
        console.error('[Dashboard Calendar] Failed to load:', error)
        return null
      })

// ─── InstrumentHeader ─────────────────────────────────────────────────────────

interface InstrumentHeaderProps {
  instrumentId: number
  instrumentLabel: string
  serialNumber?: string | null
  instrumentTypeName?: string | null
  instrumentTypeImageUrl?: string | null
  location?: string | null
  viewInstrumentHref: string
  viewMaintenanceLogsHref: string
  hideNonCompliance: boolean
  hideNonComplianceDisabled: boolean
  ncOpenCount: number
  onHideNonComplianceChange: (hide: boolean) => void
  tags?: Array<{ id: number; name: string }>
  showCalendar: boolean
  onToggleCalendar: () => void
}

const InstrumentHeader = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  instrumentTypeName,
  instrumentTypeImageUrl,
  location,
  viewInstrumentHref,
  viewMaintenanceLogsHref,
  hideNonCompliance,
  hideNonComplianceDisabled,
  ncOpenCount,
  onHideNonComplianceChange,
  tags,
  showCalendar,
  onToggleCalendar,
}: InstrumentHeaderProps) => {
  return (
    <div className="flex flex-col gap-3 border-b pb-3 mb-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-stretch gap-3 sm:gap-4 min-w-0 flex-1">
        {instrumentTypeImageUrl ? (
          <img
            src={instrumentTypeImageUrl}
            alt={instrumentTypeName ?? instrumentLabel}
            className="w-24 max-h-24 shrink-0 rounded-md object-contain"
          />
        ) : (
          <div className="flex w-24 shrink-0 items-center justify-center rounded-md bg-muted">
            <ImageOff className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
            <Link
              href={viewInstrumentHref}
              className="font-bold text-sm sm:text-base text-primary underline-offset-4 hover:underline"
            >
              {instrumentLabel}
            </Link>
            <Link
              href={viewMaintenanceLogsHref}
              className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-primary"
            >
              {t('taskQueue.card.viewLogs')} ›
            </Link>
          </div>
          <div className="mt-1 flex flex-col gap-0.5 text-xs">
            {serialNumber && (
              <span>
                <span className="text-muted-foreground">{t('taskQueue.card.serialNumber')}:</span>{' '}
                <span className="font-medium text-foreground">{serialNumber}</span>
              </span>
            )}
            {location && (
              <span>
                <span className="text-muted-foreground">{t('taskQueue.card.location')}:</span>{' '}
                <span className="font-medium text-foreground">{location}</span>
              </span>
            )}
            {instrumentTypeName && (
              <span>
                <span className="text-muted-foreground">{t('taskQueue.card.instrumentType')}:</span>{' '}
                <span className="font-medium text-foreground">{instrumentTypeName}</span>
              </span>
            )}
          </div>
          {tags && tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 h-4 font-normal"
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={onToggleCalendar}
            className="mt-2 text-xs gap-1 w-fit"
          >
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', showCalendar && 'rotate-180')}
            />
            {showCalendar ? t('taskQueue.calendar.hide') : t('taskQueue.calendar.show')}
          </Button>
        </div>
      </div>
      {ncOpenCount > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-t pt-2 sm:border-t-0 sm:pt-0 sm:flex-col sm:items-end">
          <HideNonComplianceToggle
            instrumentId={instrumentId}
            hideNonCompliance={hideNonCompliance}
            ncOpenCount={ncOpenCount}
            disabled={hideNonComplianceDisabled}
            onHideNonComplianceChange={onHideNonComplianceChange}
          />
          {hideNonComplianceDisabled && (
            <span className="text-xs text-muted-foreground">
              {t('taskQueue.toggle.appliedToAllInstruments')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ─── InstrumentGroup ──────────────────────────────────────────────────────────

interface InstrumentGroupProps {
  instrumentId: number
  tasks: WebDashboardTaskQueueCard[]
  ncItems: WebNonComplianceCard[]
  loggerOptions: LoggerOption[]
  logAuthenticationCode: string | null
  canReview: boolean
  dismissingId: number | null
  onActionStart: (id: number) => void
  instrumentTags: Array<{ id: number; name: string }>
  taskTotal: number
  ncOpenCount: number
  hideNonComplianceAll: boolean
  tailFilterParams: Record<string, unknown>
}

const CardPlaceholders = ({ count, className }: { count: number; className: string }) =>
  Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      aria-hidden
      data-testid="card-placeholder"
      className={cn('animate-pulse rounded-lg bg-muted', className)}
    />
  ))

interface ShowMoreButtonProps {
  remainingCount: number
  loading: boolean
  onClick: () => void
}

const ShowMoreButton = ({ remainingCount, loading, onClick }: ShowMoreButtonProps) => {
  if (remainingCount === 0 && !loading) return null

  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full text-muted-foreground"
      onClick={onClick}
      disabled={loading}
    >
      {loading
        ? t('taskQueue.overflow.loading')
        : t('taskQueue.overflow.showMore', { count: remainingCount })}
    </Button>
  )
}

const InstrumentGroup = ({
  instrumentId,
  tasks,
  ncItems,
  loggerOptions,
  logAuthenticationCode,
  canReview,
  dismissingId,
  onActionStart,
  instrumentTags,
  taskTotal,
  ncOpenCount,
  hideNonComplianceAll,
  tailFilterParams,
}: InstrumentGroupProps) => {
  const [hideNonCompliance, setHideNonCompliance] = useState(false)
  const nonComplianceHidden = hideNonComplianceAll || hideNonCompliance
  const groupRef = useRef<HTMLDivElement>(null)
  const isNearViewport = useNearViewport(groupRef)
  const taskTail = useInstrumentTail({
    instrumentId,
    initialRecords: tasks,
    total: taskTotal,
    path: tasksDashboardOverflowPath(),
    filterParams: tailFilterParams,
    enabled: isNearViewport,
  })
  const ncTail = useInstrumentTail({
    instrumentId,
    initialRecords: ncItems,
    total: ncOpenCount,
    path: tasksDashboardNcOverflowPath(),
    filterParams: tailFilterParams,
    enabled: isNearViewport && !nonComplianceHidden,
  })
  const [showCalendar, setShowCalendar] = useState(false)
  // Bumped after any NC review action succeeds, so the embedded calendar (which fetches
  // and caches its own data outside Inertia props) refreshes without a manual page reload
  // (REBUILD-409). Also reload Inertia presence props so empty instruments leave (E50-S3).
  const [calendarRefreshSignal, setCalendarRefreshSignal] = useState(0)
  const handleReviewSuccess = useCallback(() => {
    reloadTasksDashboardProps()
    setCalendarRefreshSignal((previous) => previous + 1)
  }, [])
  const handleToggleCalendar = useCallback(() => {
    setShowCalendar((prev) => !prev)
  }, [])

  const allTasks = taskTail.records
  const allNcItems = ncTail.records

  // Flattened ribbon order (REBUILD-501): no status group headers, but keep the historical
  // Overdue → Due Now → Ready ordering by walking the sections and concatenating their tasks.
  const orderedTasks = useMemo(
    () => STATUS_SECTIONS.flatMap(({ code }) => tasksForSection(code, allTasks)),
    [allTasks],
  )

  const first = allTasks[0] ?? ncItems[0]
  if (!first) return null

  const location = [first.siteName, first.departmentName, first.unitName]
    .filter(Boolean)
    .join(' / ')

  return (
    <div ref={groupRef} className="rounded-lg border p-4 space-y-3">
      <InstrumentHeader
        instrumentId={instrumentId}
        instrumentLabel={first.instrumentLabel}
        serialNumber={first.serialNumber}
        instrumentTypeName={first.instrumentTypeName}
        instrumentTypeImageUrl={first.instrumentTypeImageUrl}
        location={location}
        viewInstrumentHref={first.viewInstrumentHref}
        viewMaintenanceLogsHref={first.viewMaintenanceLogsHref}
        hideNonCompliance={nonComplianceHidden}
        hideNonComplianceDisabled={hideNonComplianceAll}
        ncOpenCount={ncOpenCount}
        onHideNonComplianceChange={setHideNonCompliance}
        tags={instrumentTags}
        showCalendar={showCalendar}
        onToggleCalendar={handleToggleCalendar}
      />

      {/* Calendar — interactive (month navigation + day-click), lazy-loaded on toggle.
          Reuses the Reports-tab panel so both surfaces share one interactive calendar
          (legacy parity: DASHBOARD_INSTRUMENTS uses the same calendarView). */}
      {showCalendar && (
        <div className="border-t pt-3">
          <InstrumentCalendarViewPanel
            instrumentId={instrumentId}
            instrumentLabel={first.instrumentLabel}
            serialNumber={first.serialNumber}
            initialCalendarData={null}
            siteTimeZone={first.timeZoneName}
            isActive={showCalendar}
            hideSignOffHistory
            fetchCalendarData={buildDashboardCalendarFetchFn(instrumentId)}
            refreshSignal={calendarRefreshSignal}
          />
        </div>
      )}

      {/* Non-compliance items — only when the instrument has NCs (legacy: nonComplianceCount > 0).
          Hide Non-Compliance collapses this block; never show an empty-state message (REBUILD-442). */}
      {!nonComplianceHidden && allNcItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-neutral-500 mb-2">
            {t('taskQueue.sections.nonCompliance')}
          </p>
          {allNcItems.map((record) => (
            <NonComplianceCard
              key={record.id}
              record={record}
              isDismissing={dismissingId === record.id}
              onActionStart={onActionStart}
              selectMode={false}
              selected={false}
              onToggleSelect={() => undefined}
              canReview={canReview}
              hideInstrumentHeader
              // Legacy parity: timeline hidden on DASHBOARD_INSTRUMENTS (compliance-card.template.html:128).
              showTimeline={false}
              onReviewSuccess={handleReviewSuccess}
            />
          ))}
          <CardPlaceholders count={ncTail.placeholderCount} className="h-24" />
          <ShowMoreButton
            remainingCount={ncTail.remainingCount}
            loading={ncTail.loadingMore}
            onClick={ncTail.showMore}
          />
        </div>
      )}

      {/* Task ribbons — flat list, no status group headers (REBUILD-501).
          Status is shown once, on each ribbon; ordering is preserved by walking the status
          sections in Overdue → Due Now → Ready order and concatenating their ribbons. */}
      {allTasks.length > 0 && (
        <div
          className={cn(
            'space-y-2',
            !nonComplianceHidden && allNcItems.length > 0 && 'pt-2 border-t',
          )}
        >
          {orderedTasks.map((record) => (
            <TaskQueueCard
              key={record.id}
              record={record}
              loggerOptions={loggerOptions}
              logAuthenticationCode={logAuthenticationCode}
              hideInstrumentHeader
            />
          ))}
          <CardPlaceholders count={taskTail.placeholderCount} className="h-14" />
          <ShowMoreButton
            remainingCount={taskTail.remainingCount}
            loading={taskTail.loadingMore}
            onClick={taskTail.showMore}
          />
        </div>
      )}
    </div>
  )
}

// ─── TaskQueueDashboardPage ───────────────────────────────────────────────────

// Pointer events on an option label target the text node, which is not an Element.
// Treating that as an outside click closed the controlled dropdown before the tick applied.
const elementFromPointerTarget = (target: EventTarget | null): Element | null => {
  if (target instanceof Element) return target
  if (target instanceof Node) return target.parentElement
  return null
}

const TaskQueueDashboardPage = () => {
  const {
    taskRecords,
    ncRecords,
    taskCountsByInstrument,
    ncCountsByInstrument,
    dashboardFingerprint,
    meta,
    filters,
    siteOptions,
    departmentOptions,
    unitOptions,
    tagOptions,
    instrumentOptions,
    hasPendingTasks,
    loggerOptions,
    logAuthenticationCode,
    instrumentTagsMap,
  } = usePage<Props>().props
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null
  const [hideNonComplianceAll, setHideNonComplianceAll] = useState(() =>
    readHideNonComplianceAll(currentTenantId, currentUserId),
  )
  const handleHideNonComplianceAllChange = (hideAll: boolean) => {
    setHideNonComplianceAll(hideAll)
    writeHideNonComplianceAll(currentTenantId, currentUserId, hideAll)
  }

  useBreadcrumbs([{ label: t('nav.dashboard') }, { label: t('nav.tasks') }])

  // The poll fetches only dashboardFingerprint. Card props (tasks, NC cards, totals — keys
  // shared with reloadTasksDashboardProps) reload when it differs from the fingerprint the
  // rendered cards came with. A reload that brings new cards (action refresh, filter visit)
  // also brings their fingerprint, so it never triggers a second reload.
  useStatusPoll(STATUS_POLL_INTERVAL_MS, TASKS_DASHBOARD_STATUS_POLL_OPTIONS)
  const [cardsFingerprint, setCardsFingerprint] = useState(dashboardFingerprint)
  const [cardsTaskRecords, setCardsTaskRecords] = useState(taskRecords)
  if (cardsTaskRecords !== taskRecords) {
    setCardsTaskRecords(taskRecords)
    setCardsFingerprint(dashboardFingerprint)
  }
  useEffect(() => {
    if (dashboardFingerprint !== cardsFingerprint) reloadTasksDashboardProps()
  }, [dashboardFingerprint, cardsFingerprint])

  // The overflow endpoints filter like the page (TasksController#service_params / #nc_params).
  // Built from the applied server filters: the URL's instrument_id[] would clash with the
  // tail's own instrument_id.
  const tailFilterParams = useMemo(
    () => ({
      site_id: filters.siteIds,
      department_id: filters.departmentIds,
      unit_id: filters.unitIds,
      search: filters.search,
      tag_ids: filters.tagIds,
    }),
    [filters],
  )

  const canReview = useCan(Resources.COMPLIANCE_REPORT, Actions.UPDATE)
  const [dismissingId, setDismissingId] = useState<number | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const sheetOpenRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(filters.siteIds ?? [])
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>(filters.departmentIds ?? [])
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(filters.unitIds ?? [])
  const [search, setSearch] = useState(filters.search || '')
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(filters.tagIds ?? [])
  const [selectedInstrumentIds, setSelectedInstrumentIds] = useState<number[]>(
    filters.instrumentIds ?? [],
  )
  const [isNavigating, setIsNavigating] = useState(false)
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null)
  const expandedFilterRef = useRef<string | null>(null)
  useEffect(() => {
    expandedFilterRef.current = expandedFilter
  }, [expandedFilter])

  useEffect(() => {
    if (!expandedFilter) return

    const handleClickOutside = (event: PointerEvent) => {
      // Both the desktop bar and the mobile sheet render a filter accordion. A tick inside
      // either one must not count as an outside click, or the dropdown closes before the option
      // click applies.
      const element = elementFromPointerTarget(event.target)
      if (element?.closest('[data-filter-accordion]')) return
      setExpandedFilter(null)
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [expandedFilter])

  const { beginVisit } = useFilterVisitGuard()

  const navigate = useCallback(
    (
      overrides: {
        siteIds?: number[]
        departmentIds?: number[]
        unitIds?: number[]
        search?: string
        tagIds?: number[]
        instrumentIds?: number[]
        page?: number
      },
      mode: FilterVisitMode = 'default',
    ) => {
      const sites = overrides.siteIds ?? selectedSiteIds
      const depts = overrides.departmentIds ?? selectedDeptIds
      const units = overrides.unitIds ?? selectedUnitIds
      const q = (overrides.search ?? search).trim()
      const tags = overrides.tagIds ?? selectedTagIds
      const instruments = overrides.instrumentIds ?? selectedInstrumentIds

      const params = new URLSearchParams()
      appendArrayOrgFilterParams(params, {
        siteIds: sites,
        departmentIds: depts,
        unitIds: units,
      })
      if (q) params.set('search', q)
      if (overrides.page != null) params.set('page', String(overrides.page))
      tags.forEach((id) => params.append('tag_ids[]', String(id)))
      instruments.forEach((id) => params.append('instrument_id[]', String(id)))

      // Persist only shared location filters (not search / tags / instruments).
      writeLocationFilters(currentTenantId, currentUserId, {
        siteIds: sites,
        departmentIds: depts,
        unitIds: units,
      })

      const isSearchUpdate = 'search' in overrides
      // A cleared search remounts from server props so no stale typed query survives.
      const keepsTypedSearch = isSearchUpdate && q !== ''
      const query = params.size ? `?${params.toString()}` : ''
      const href = `${tasksDashboardPath()}${query}`
      const visit = beginVisit(href, resolveFilterVisitMode(sheetOpenRef.current, mode))
      if (!visit) return

      setIsNavigating(true)
      router.visit(href, {
        preserveState: visit.keepDropdownOpen ? true : keepsTypedSearch,
        preserveScroll: visit.keepDropdownOpen ? true : isSearchUpdate,
        onSuccess: () => {
          if (keepsTypedSearch) searchInputRef.current?.focus()
        },
        onFinish: () => {
          if (visit.isCurrent()) setIsNavigating(false)
        },
      })
    },
    [
      selectedSiteIds,
      selectedDeptIds,
      selectedUnitIds,
      search,
      selectedTagIds,
      selectedInstrumentIds,
      currentTenantId,
      currentUserId,
      beginVisit,
    ],
  )

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 ||
      (filters.departmentIds?.length ?? 0) > 0 ||
      (filters.unitIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: filters.siteIds ?? [],
      departmentIds: filters.departmentIds ?? [],
      unitIds: filters.unitIds ?? [],
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDeptIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigate({
        siteIds: hydrated.siteIds,
        departmentIds: hydrated.departmentIds,
        unitIds: hydrated.unitIds,
      })
    },
  })

  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions, selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions, selectedSiteIds, selectedDeptIds),
    [unitOptions, selectedSiteIds, selectedDeptIds],
  )

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions,
    unitOptions,
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDeptIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDeptIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const handleSiteCommit = (newSiteIds: number[]) =>
    navigate(orgCascade.changeSites(newSiteIds), 'commit')

  const handleDepartmentCommit = (newDepartmentIds: number[]) =>
    navigate(orgCascade.changeDepartments(newDepartmentIds), 'commit')

  const handleSelectionCommit = useCallback(
    (overrides: Parameters<typeof navigate>[0]) => {
      navigate(overrides, 'commit')
    },
    [navigate],
  )

  const debouncedNavigate = useDebouncedCallback((searchValue: string) => {
    navigate({ search: searchValue })
  }, 400)

  const closeSheetWithCleanup = () => {
    if (!sheetOpenRef.current) return
    sheetOpenRef.current = false
    debouncedNavigate.cancel()
    navigateRef.current(orgCascade.pruneSelection(), 'sheet-close')
    setSheetOpen(false)
  }

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearch('')
      debouncedNavigate.cancel()
      navigate({ search: '' })
      return
    }
    setSearch(value)
    debouncedNavigate(value)
  }

  const resetFilterState = () => {
    setSelectedSiteIds([])
    setSelectedDeptIds([])
    setSelectedUnitIds([])
    setSearch('')
    setSelectedTagIds([])
    setSelectedInstrumentIds([])
  }

  const resetSheetFilters = () => {
    debouncedNavigate.cancel()
    resetFilterState()
    navigate({
      siteIds: [],
      departmentIds: [],
      unitIds: [],
      search: '',
      tagIds: [],
      instrumentIds: [],
    })
  }

  const clearFilters = () => {
    debouncedNavigate.cancel()
    resetFilterState()
    clearLocationFilters(currentTenantId, currentUserId)
    const params = new URLSearchParams(clearOrgFiltersParams())
    const href = `${tasksDashboardPath()}?${params.toString()}`
    const visit = beginVisit(href, 'default')
    if (!visit) return
    setIsNavigating(true)
    router.visit(href, {
      preserveState: false,
      onFinish: () => {
        if (visit.isCurrent()) setIsNavigating(false)
      },
    })
  }

  const goToPage = (page: number) => {
    debouncedNavigate.cancel()
    navigate({ page })
  }

  const activeFilterCount = [
    selectedSiteIds.length > 0,
    selectedDeptIds.length > 0,
    selectedUnitIds.length > 0,
    search,
    selectedTagIds.length > 0,
    selectedInstrumentIds.length > 0,
  ].filter(Boolean).length

  // Build instrument groups: union of instruments from tasks + NC records
  const instrumentOrder: number[] = []
  const tasksByInstrument = new Map<number, WebDashboardTaskQueueCard[]>()
  const ncByInstrument = new Map<number, WebNonComplianceCard[]>()

  taskRecords.forEach((record) => {
    const id = record.instrumentId ?? 0
    if (!tasksByInstrument.has(id)) {
      instrumentOrder.push(id)
      tasksByInstrument.set(id, [])
    }
    tasksByInstrument.get(id)!.push(record)
  })

  ncRecords.forEach((record) => {
    const id = record.instrumentId
    if (!ncByInstrument.has(id) && !tasksByInstrument.has(id)) {
      instrumentOrder.push(id)
    }
    if (!ncByInstrument.has(id)) ncByInstrument.set(id, [])
    ncByInstrument.get(id)!.push(record)
  })

  const isEmpty = taskRecords.length === 0 && ncRecords.length === 0

  // Only the desktop search owns the focus ref: the mobile sheet copy unmounts on close and
  // would null a shared ref while the desktop input stays mounted.
  const renderFilterFields = (
    focusableSearchInputRef?: RefObject<HTMLInputElement | null>,
    hideAllToggleId = 'hide-non-compliance-all',
  ) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-1">
        <Label className="text-xs text-muted-foreground">
          {t('taskQueue.filters.filterSearch')}
        </Label>
        <Input
          ref={focusableSearchInputRef}
          placeholder={t('taskQueue.filters.searchPlaceholder')}
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="lg:min-w-[200px] bg-background"
          aria-label={t('taskQueue.filters.searchPlaceholder')}
        />
      </div>

      {/* Inline accordion filters — shared for mobile and desktop */}
      <div data-filter-accordion="" className="contents">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t('taskQueue.filters.filterSite')}
          </Label>
          <MultiSelectCombobox
            showBadges={false}
            emptyMessage={t('taskQueue.filters.noResults')}
            options={siteOptions.map((s) => ({ value: s.value, label: s.label }))}
            selected={selectedSiteIds}
            onChange={orgCascade.changeSites}
            onClose={handleSiteCommit}
            placeholder={t('taskQueue.filters.allSites')}
            searchPlaceholder={t('taskQueue.filters.searchSites')}
            expanded={expandedFilter === 'sites'}
            onToggle={() => setExpandedFilter((prev) => (prev === 'sites' ? null : 'sites'))}
            className="lg:w-48"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t('taskQueue.filters.filterDepartment')}
          </Label>
          <MultiSelectCombobox
            showBadges={false}
            emptyMessage={t('taskQueue.filters.noResults')}
            options={filteredDepartmentOptions}
            selected={selectedDeptIds}
            onChange={orgCascade.changeDepartments}
            onClose={handleDepartmentCommit}
            placeholder={t('taskQueue.filters.allDepartments')}
            searchPlaceholder={t('taskQueue.filters.searchDepartments')}
            expanded={expandedFilter === 'departments'}
            onToggle={() =>
              setExpandedFilter((prev) => (prev === 'departments' ? null : 'departments'))
            }
            className="lg:w-44"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t('taskQueue.filters.filterUnit')}
          </Label>
          <MultiSelectCombobox
            showBadges={false}
            emptyMessage={t('taskQueue.filters.noResults')}
            options={filteredUnitOptions}
            selected={selectedUnitIds}
            onChange={setSelectedUnitIds}
            onClose={(selected) => handleSelectionCommit({ unitIds: selected })}
            placeholder={t('taskQueue.filters.allUnits')}
            searchPlaceholder={t('taskQueue.filters.searchUnits')}
            expanded={expandedFilter === 'units'}
            onToggle={() => setExpandedFilter((prev) => (prev === 'units' ? null : 'units'))}
            className="lg:w-36"
          />
        </div>

        {tagOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">
              {t('taskQueue.filters.filterTags')}
            </Label>
            <MultiSelectCombobox
              showBadges={false}
              emptyMessage={t('taskQueue.filters.noResults')}
              options={tagOptions}
              selected={selectedTagIds}
              onChange={setSelectedTagIds}
              onClose={(selected) => handleSelectionCommit({ tagIds: selected })}
              placeholder={t('taskQueue.filters.allTags')}
              searchPlaceholder={t('taskQueue.filters.searchTags')}
              expanded={expandedFilter === 'tags'}
              onToggle={() => setExpandedFilter((prev) => (prev === 'tags' ? null : 'tags'))}
              className="lg:w-48"
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t('taskQueue.filters.filterInstruments')}
          </Label>
          <MultiSelectCombobox
            showBadges={false}
            emptyMessage={t('taskQueue.filters.noResults')}
            options={instrumentOptions}
            selected={selectedInstrumentIds}
            onChange={setSelectedInstrumentIds}
            onClose={(selected) => handleSelectionCommit({ instrumentIds: selected })}
            placeholder={t('taskQueue.filters.allInstruments')}
            searchPlaceholder={t('taskQueue.filters.searchInstruments')}
            expanded={expandedFilter === 'instruments'}
            onToggle={() =>
              setExpandedFilter((prev) => (prev === 'instruments' ? null : 'instruments'))
            }
            className="lg:w-48"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-3 sm:col-span-2 lg:basis-full">
        <div className="flex items-center gap-2">
          <Switch
            id={hideAllToggleId}
            checked={hideNonComplianceAll}
            onCheckedChange={handleHideNonComplianceAllChange}
          />
          <Label htmlFor={hideAllToggleId} className="cursor-pointer text-sm">
            {t('taskQueue.filters.hideAllNonCompliance')}
          </Label>
        </div>
        {hideNonComplianceAll && (
          <span className="text-xs text-muted-foreground">
            {t('taskQueue.filters.hideAllNonComplianceHint')}
          </span>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <DashboardTabs />

      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold sm:text-2xl">{t('taskQueue.dashboard.title')}</h1>

        <div className="flex items-center gap-2 lg:hidden">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground px-2"
              onClick={clearFilters}
            >
              {t('taskQueue.filters.clear')}
            </Button>
          )}
          <Sheet
            open={sheetOpen}
            onOpenChange={(open) => {
              if (!open) {
                closeSheetWithCleanup()
              } else {
                debouncedNavigate.cancel()
                setSheetOpen(true)
                sheetOpenRef.current = true
              }
            }}
          >
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {t('taskQueue.filters.searchButton')}
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="rounded-t-xl px-4 pt-4 pb-8"
              title={t('taskQueue.filters.searchButton')}
              hideCloseButton
              onInteractOutside={(event) => {
                event.preventDefault()
                if (expandedFilterRef.current) {
                  setExpandedFilter(null)
                } else {
                  closeSheetWithCleanup()
                }
              }}
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold">{t('taskQueue.filters.searchButton')}</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={resetSheetFilters}
                  >
                    {t('taskQueue.filters.reset')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('taskQueue.filters.done')}
                    onClick={closeSheetWithCleanup}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="max-h-[60vh] space-y-3 overflow-y-auto overscroll-contain">
                {renderFilterFields(undefined, 'hide-non-compliance-all-mobile')}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop filter panel */}
      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {renderFilterFields(searchInputRef, 'hide-non-compliance-all-desktop')}
          {activeFilterCount > 0 && (
            <div className="flex items-center lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('taskQueue.filters.clear')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {isNavigating ? (
        <div
          className="flex flex-col items-center gap-3 py-16"
          data-testid="dashboard-filter-loading"
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">{t('taskQueue.dashboard.loading')}</p>
        </div>
      ) : isEmpty && !hasPendingTasks ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <CheckSquare className="h-5 w-5 text-muted-foreground" />
          </span>
          <p className="text-sm text-muted-foreground">
            {activeFilterCount > 0
              ? t('taskQueue.dashboard.noDataWithFilters')
              : t('taskQueue.dashboard.allUpToDate')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {instrumentOrder.map((instrumentId) => (
            <InstrumentGroup
              key={instrumentId}
              instrumentId={instrumentId}
              tasks={tasksByInstrument.get(instrumentId) ?? []}
              ncItems={ncByInstrument.get(instrumentId) ?? []}
              loggerOptions={loggerOptions}
              logAuthenticationCode={logAuthenticationCode}
              canReview={canReview}
              dismissingId={dismissingId}
              onActionStart={setDismissingId}
              instrumentTags={instrumentTagsMap[instrumentId] ?? []}
              taskTotal={taskCountsByInstrument[instrumentId] ?? 0}
              ncOpenCount={ncCountsByInstrument[instrumentId] ?? 0}
              hideNonComplianceAll={hideNonComplianceAll}
              tailFilterParams={tailFilterParams}
            />
          ))}
        </div>
      )}

      <DataPagination
        meta={meta}
        onPageChange={goToPage}
        noun={t('taskQueue.dashboard.paginationNoun')}
      />
    </div>
  )
}

export default TaskQueueDashboardPage
